import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import classnames from 'classnames';
import { empleadoService } from 'services/empleadoService';
import { usuariosService } from 'services/usuariosService';

function EmpleadoForm({ isOpen, toggle, empleado, empresaId, onSuccess }) {
  const [activeTab, setActiveTab] = useState('1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioSearch, setUsuarioSearch] = useState('');
  const [showUsuarioDropdown, setShowUsuarioDropdown] = useState(false);

  const [formData, setFormData] = useState({
    Nombre: '',
    Paterno: '',
    Materno: '',
    RFC: '',
    CURP: '',
    FechaNacimiento: '',
    Sexo: 'M',
    Foto: '',
    UsuarioID: '',
    EmpresaID: empresaId || '',
    // Dirección
    Direccion: '',
    Localidad: '',
    Ciudad: '',
    Estado: '',
    Pais: 'México',
    CodigoPostal: '',
    NumeroExterior: '',
    NumeroInterior: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadUsuarios();
      if (empleado) {
        // Formatear la fecha para el input tipo date (YYYY-MM-DD)
        let fechaFormat = '';
        const rawFecha = empleado.FechaNacimiento || empleado.fechaNacimiento;
        if (rawFecha) {
          try {
            fechaFormat = new Date(rawFecha).toISOString().split('T')[0];
          } catch (e) {
            fechaFormat = '';
          }
        }

        const direccion = empleado.Direccion || empleado.direccion || {};
        const usuario = empleado.Usuario || empleado.usuario || {};

        setFormData({
          Nombre: empleado.Nombre || empleado.nombre || '',
          Paterno: empleado.Paterno || empleado.paterno || '',
          Materno: empleado.Materno || empleado.materno || '',
          RFC: empleado.RFC || empleado.rfc || '',
          CURP: empleado.CURP || empleado.curp || '',
          FechaNacimiento: fechaFormat,
          Sexo: empleado.Sexo || empleado.sexo || 'M',
          Foto: empleado.Foto || empleado.foto || '',
          UsuarioID: empleado.UsuarioID || empleado.usuarioID || usuario.UsuarioID || usuario.usuarioID || '',
          EmpresaID: empleado.EmpresaID || empleado.empresaID || empresaId || '',

          Direccion: direccion.Direccion || direccion.direccion || '',
          Localidad: direccion.Localidad || direccion.localidad || '',
          Ciudad: direccion.Ciudad || direccion.ciudad || '',
          Estado: direccion.Estado || direccion.estado || '',
          Pais: direccion.Pais || direccion.pais || 'México',
          CodigoPostal: direccion.CodigoPostal || direccion.codigoPostal || '',
          NumeroExterior: direccion.NumeroExterior || direccion.numeroExterior || '',
          NumeroInterior: direccion.NumeroInterior || direccion.numeroInterior || ''
        });
      } else {
        // Reset en modo creación
        setFormData({
          Nombre: '',
          Paterno: '',
          Materno: '',
          RFC: '',
          CURP: '',
          FechaNacimiento: '',
          Sexo: 'M',
          Foto: '',
          UsuarioID: '',
          EmpresaID: empresaId || '',
          Direccion: '',
          Localidad: '',
          Ciudad: '',
          Estado: '',
          Pais: 'México',
          CodigoPostal: '',
          NumeroExterior: '',
          NumeroInterior: ''
        });
        setUsuarioSearch('');
      }
      setActiveTab('1');
      setError(null);
    }
  }, [isOpen, empleado, empresaId]);

  const loadUsuarios = async () => {
    try {
      if (!empresaId) return;
      const res = await usuariosService.getAll(empresaId);
      if (res.success && res.data) {
        const data = Array.isArray(res.data) ? res.data : [];
        setUsuarios(data);

        // Si estamos editando y tenemos un UsuarioID
        if (empleado) {
          const tempUsuario = empleado.Usuario || empleado.usuario || {};
          const expectedId = empleado.UsuarioID || empleado.usuarioID || tempUsuario.UsuarioID || tempUsuario.usuarioID;
          if (expectedId) {
            const u = data.find(x => (x.UsuarioID || x.usuarioID) === expectedId);
            if (u) {
              setUsuarioSearch(u.Nombre || u.nombre || u.Correo || u.correo || '');
            }
          }
        }
      }
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const validateForm = () => {
    const faltantes = [];
    if (!formData.Nombre) faltantes.push("Nombre");
    if (!formData.Paterno) faltantes.push("Paterno");
    if (!formData.RFC) faltantes.push("RFC");
    if (!formData.CURP) faltantes.push("CURP");
    if (!formData.FechaNacimiento) faltantes.push("FechaNacimiento");

    if (faltantes.length > 0) {
      setError(`Faltan campos principales obligatorios: ${faltantes.join(', ')}.`);
      setActiveTab('1');
      return false;
    }

    if (!empleado) {
      // Obligatoria la direccion
      if (!formData.Direccion || !formData.Ciudad || !formData.Estado || !formData.CodigoPostal || !formData.NumeroExterior) {
        setError('Al crear un empleado por primera vez, la dirección básica es obligatoria (Calle, Ciudad, Estado, C.P. y Número Exterior).');
        setActiveTab('2');
        return false;
      }
    }

    if (!formData.UsuarioID) {
      setError('Selecciona un Usuario asociado para el empleado.');
      setActiveTab('1');
      return false;
    }

    setError(null);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payloadEmpleado = {
        Nombre: formData.Nombre,
        Paterno: formData.Paterno,
        Materno: formData.Materno,
        RFC: formData.RFC,
        CURP: formData.CURP,
        FechaNacimiento: formData.FechaNacimiento,
        Sexo: formData.Sexo,
        Foto: formData.Foto,
        UsuarioID: formData.UsuarioID ? parseInt(formData.UsuarioID, 10) : 0,
        EmpresaID: parseInt(formData.EmpresaID, 10)
      };

      const payloadDireccion = {
        Direccion: formData.Direccion,
        Localidad: formData.Localidad,
        Ciudad: formData.Ciudad,
        Estado: formData.Estado,
        Pais: formData.Pais,
        CodigoPostal: String(formData.CodigoPostal),
        NumeroExterior: String(formData.NumeroExterior),
        NumeroInterior: String(formData.NumeroInterior) || ''
      };

      // Si es un empleado existente (Mismo Endpoint para emp, otro para dir)
      if (empleado) {
        const id = empleado.EmpleadoID || empleado.empleadoID;
        // 1. Actualizar Empleado
        const resEmp = await empleadoService.update(id, payloadEmpleado);
        if (!resEmp.success) {
          setError(resEmp.message || 'Error al actualizar el empleado');
          setLoading(false);
          return;
        }

        // 2. Actualizar Dirección
        payloadDireccion.EmpleadoID = id;
        const resDir = await empleadoService.updateDireccion(id, payloadDireccion);
        if (!resDir.success) {
          console.warn("Empleado modificado pero la dirección falló.", resDir);
          setError(resDir.message || 'El empleado se actualizó, pero hubo un error al actualizar la dirección.');
          setLoading(false);
          return;
        }

        onSuccess();
        toggle();
      } else {
        // Creación
        const res = await empleadoService.create(payloadEmpleado);
        if (res.success && res.data) {
          const newEmpleadoId = res.data.EmpleadoID || res.data.empleadoID;

          if (newEmpleadoId) {
            payloadDireccion.EmpleadoID = newEmpleadoId;
            const dirRes = await empleadoService.createDireccion(payloadDireccion);
            if (!dirRes.success) {
              console.warn("Empleado creado pero la dirección falló.", dirRes);
              // Manejo parcial de error o notificación
            }
          }

          onSuccess();
          toggle();
        } else {
          setError(res.message || 'Error al crear el empleado');
        }
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error inesperado al conectarse al servicio.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {empleado ? 'Editar Empleado' : 'Nuevo Empleado'}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}

        <Nav tabs>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '1' })}
              onClick={() => toggleTab('1')}
              cssModule={{ cursor: 'pointer' }}
              href="#"
            >
              Datos Personales
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '2' })}
              onClick={() => toggleTab('2')}
              cssModule={{ cursor: 'pointer' }}
              href="#"
            >
              Dirección
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab} className="mt-3">
          <TabPane tabId="1">
            <Form>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label for="Nombre">Nombre(s) *</Label>
                    <Input type="text" name="Nombre" id="Nombre" value={formData.Nombre} onChange={handleInputChange} required />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="Paterno">Paterno *</Label>
                    <Input type="text" name="Paterno" id="Paterno" value={formData.Paterno} onChange={handleInputChange} required />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="Materno">Materno</Label>
                    <Input type="text" name="Materno" id="Materno" value={formData.Materno} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="RFC">RFC *</Label>
                    <Input type="text" name="RFC" id="RFC" value={formData.RFC} onChange={handleInputChange} required maxLength="13" />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="CURP">CURP *</Label>
                    <Input type="text" name="CURP" id="CURP" value={formData.CURP} onChange={handleInputChange} required maxLength="18" />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label for="FechaNacimiento">Fecha de Nacimiento *</Label>
                    <Input type="date" name="FechaNacimiento" id="FechaNacimiento" value={formData.FechaNacimiento} onChange={handleInputChange} required />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="Sexo">Sexo</Label>
                    <Input type="select" name="Sexo" id="Sexo" value={formData.Sexo} onChange={handleInputChange}>
                      <option value="M">Masculino</option>
                      <option value="F">Femenino</option>
                      <option value="O">Otro</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup style={{ position: 'relative' }}>
                    <Label for="UsuarioID">Usuario Asociado *</Label>
                    <Input
                      type="text"
                      placeholder="Buscar usuario..."
                      value={usuarioSearch}
                      onChange={(e) => {
                        setUsuarioSearch(e.target.value);
                        setShowUsuarioDropdown(true);
                        if (formData.UsuarioID) setFormData(prev => ({ ...prev, UsuarioID: '' }));
                      }}
                      onFocus={() => setShowUsuarioDropdown(true)}
                      onBlur={() => setTimeout(() => setShowUsuarioDropdown(false), 200)}
                    />
                    {showUsuarioDropdown && (
                      <div className="dropdown-menu show" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                        {usuarios
                          .filter(u => {
                            const label = (u.Nombre || u.nombre || u.Correo || u.correo || '').toLowerCase();
                            return label.includes(usuarioSearch.toLowerCase());
                          })
                          .map(u => {
                            const id = u.UsuarioID || u.usuarioID;
                            const label = u.Nombre || u.nombre || u.Correo || u.correo || `Usuario #${id}`;
                            return (
                              <button
                                type="button"
                                key={id}
                                className="dropdown-item"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, UsuarioID: id }));
                                  setUsuarioSearch(label);
                                  setShowUsuarioDropdown(false);
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                {label}
                              </button>
                            );
                          })
                        }
                        {usuarios.filter(u => {
                          const label = (u.Nombre || u.nombre || u.Correo || u.correo || '').toLowerCase();
                          return label.includes(usuarioSearch.toLowerCase());
                        }).length === 0 && (
                            <div className="dropdown-item text-muted">No se encontraron opciones</div>
                          )}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label for="Foto">URL Foto (Opcional por ahora)</Label>
                    <Input type="text" name="Foto" id="Foto" value={formData.Foto} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </TabPane>
          <TabPane tabId="2">
            <Form>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="Direccion">Calle y Colonia {!empleado && "*"}</Label>
                    <Input type="text" name="Direccion" id="Direccion" value={formData.Direccion} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label for="NumeroExterior">Núm Ext {!empleado && "*"}</Label>
                    <Input type="text" name="NumeroExterior" id="NumeroExterior" value={formData.NumeroExterior} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label for="NumeroInterior">Núm Int</Label>
                    <Input type="text" name="NumeroInterior" id="NumeroInterior" value={formData.NumeroInterior} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label for="Localidad">Localidad</Label>
                    <Input type="text" name="Localidad" id="Localidad" value={formData.Localidad} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="Ciudad">Ciudad {!empleado && "*"}</Label>
                    <Input type="text" name="Ciudad" id="Ciudad" value={formData.Ciudad} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="Estado">Estado {!empleado && "*"}</Label>
                    <Input type="text" name="Estado" id="Estado" value={formData.Estado} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="Pais">País {!empleado && "*"}</Label>
                    <Input type="text" name="Pais" id="Pais" value={formData.Pais} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="CodigoPostal">C.P. {!empleado && "*"}</Label>
                    <Input type="number" name="CodigoPostal" id="CodigoPostal" value={formData.CodigoPostal} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
              </Row>
              {!empleado && (
                <Alert color="info" className="mt-2">
                  Al registrar un empleado, la información de dirección es obligatoria inicial.
                </Alert>
              )}
            </Form>
          </TabPane>
        </TabContent>

      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancelar
        </Button>
        <Button color="success" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>{' '}
      </ModalFooter>
    </Modal>
  );
}

export default EmpleadoForm;